import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../config/theme';
import { getApiUrl } from '../config/api';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    organizationName: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    department: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${getApiUrl()}/api/auth/register-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organizationName: formData.organizationName,
          name: formData.name,
          email: formData.email,
          password: formData.password,
          department: formData.department,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao solicitar cadastro');
      }

      if (data.requiresTwoFactorEnrollment) {
        setSuccess('Empresa criada. Entre com suas credenciais para configurar a autenticação em duas etapas.');
        window.setTimeout(() => navigate('/login'), 1400);
        return;
      }
      if (!data.token || !data.user) throw new Error('Resposta de cadastro inválida');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao solicitar cadastro');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* Lado Esquerdo - Formulário */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-surface dark:bg-surface-dark overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Botão Voltar */}
          <button
            onClick={() => navigate('/login')}
            className="mb-6 flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-brand-700 dark:text-brand-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Voltar para login</span>
          </button>

          {/* Logo */}
          <div className="mb-8">
            <img src="/iasolicitar-logo.svg" alt="IaSolicitar" className="h-14 object-contain" />
          </div>

          {/* Título */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink-title dark:text-ink-title-dark mb-2">Crie sua empresa</h2>
            <p className="text-ink-subtitle dark:text-ink-subtitle-dark text-sm">
              Crie o primeiro acesso administrativo. Os dados e usuários da empresa ficam isolados das demais organizações.
            </p>
          </div>

          {/* Mensagens */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/60 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/60 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-300 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="organizationName" className="block text-sm font-medium text-ink-text dark:text-ink-text-dark mb-2">
                Nome da Empresa
              </label>
              <input
                id="organizationName"
                name="organizationName"
                type="text"
                value={formData.organizationName}
                onChange={handleChange}
                placeholder="Ex: Soltech Tecnologia Ltda"
                required
                className="w-full px-4 py-3 border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:text-ink-placeholder-dark"
              />
            </div>

            {/* Nome Completo */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-ink-text dark:text-ink-text-dark mb-2">
                Nome Completo
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Digite seu nome completo"
                required
                className="w-full px-4 py-3 border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:text-ink-placeholder-dark"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ink-text dark:text-ink-text-dark mb-2">
                E-mail de Acesso
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="seuemail@empresa.com"
                required
                className="w-full px-4 py-3 border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:text-ink-placeholder-dark"
              />
            </div>

            {/* Departamento */}
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-ink-text dark:text-ink-text-dark mb-2">
                Departamento
              </label>
              <input
                id="department"
                name="department"
                type="text"
                value={formData.department}
                onChange={handleChange}
                placeholder="Ex: Licitações, Jurídico, Compras"
                className="w-full px-4 py-3 border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:text-ink-placeholder-dark"
              />
            </div>

            {/* Senha */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ink-text dark:text-ink-text-dark mb-2">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="w-full px-4 py-3 pr-12 border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:text-ink-placeholder-dark"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirmar Senha */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2"
              >
                Confirmar Senha
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Digite a senha novamente"
                  required
                  className="w-full px-4 py-3 pr-12 border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:text-ink-placeholder-dark"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Botão Cadastrar */}
            <button
              type="submit"
              disabled={loading}
              className={`${theme.classes.button.primary} w-full py-3.5`}
            >
              {loading ? 'Criando empresa...' : 'Criar empresa e acessar'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
            <p>
              Já tem uma conta?{' '}
              <button
                onClick={() => navigate('/login')}
                className={theme.classes.link.primary}
              >
                Fazer login
              </button>
            </p>
          </div>

          {/* Aviso */}
          <div className="mt-6 p-4 bg-surface-alt dark:bg-surface-dark-alt border border-brand-200 dark:border-[rgba(203,106,30,0.30)] rounded-lg">
            <p className="text-xs text-ink-label dark:text-ink-label-dark">
              <strong className="text-brand-700 dark:text-brand-300">Importante:</strong> este cadastro cria uma organização própria e o primeiro usuário recebe o perfil de administrador.
            </p>
          </div>
        </div>
      </div>

      {/* Lado Direito - Background Gradiente */}
      <div className={`hidden lg:flex lg:w-1/2 bg-gradient-to-br ${theme.colors.gradients.backgroundDark} relative overflow-hidden`}>
        {/* Padrão de linhas diagonais */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="diagonal-lines"
                x="0"
                y="0"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <line x1="0" y1="40" x2="40" y2="0" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagonal-lines)" />
          </svg>
        </div>

        {/* Círculos decorativos */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-surface dark:bg-surface-dark opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-surface dark:bg-surface-dark opacity-5 rounded-full blur-3xl"></div>

        {/* Conteúdo centralizado */}
        <div className="relative z-10 flex items-center justify-center w-full p-16">
          <div className="text-center text-white">
            <h2 className="text-5xl font-bold mb-6">Junte-se a nós!</h2>
            <p className="text-xl opacity-90 mb-8">
              Faça parte da revolução na gestão de licitações públicas
            </p>
            <div className="grid grid-cols-1 gap-6 max-w-md mx-auto text-left">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Gestão Completa</h3>
                  <p className="text-sm opacity-80">
                    Controle total de licitações, contratos e documentos
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">IA Integrada</h3>
                  <p className="text-sm opacity-80">
                    Análise inteligente de editais com Google Gemini
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Notificações Automáticas</h3>
                  <p className="text-sm opacity-80">Alertas de prazos e renovações em tempo real</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
